import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'

// @Contants
import { CountryNames, CountriesByName } from '@/constants/country_iso_dict'
import PostalAddress from '@/utils/postalAddress'

// @Functions
import usePageUtil from '@/pages/composables/usePageUtil'
import IssueTracker from './functions'
import * as Types from './types'
import DeepDiff from 'deep-diff'

// @Components
import Page from '@/layouts/Page.vue'
import TabNav from '@/components/TabNav.vue'
import SelectableList from '@/components/SelectableList.vue'
import SelectableItem from '@/components/SelectableItem.vue'
import ControllableView from '@/layouts/components/ControllableView.vue'
import DraftEditor from './components/DraftEditor.vue'
import IssueEditor from './components/IssueEditor.vue'
import AllClearImg from '@/components/AllClearImg.vue'
import ChooseItem from '@/components/ChooseItem.vue'

import { useQuasar } from 'quasar'
import { debounce } from '@/utils'



export default /*@__PURE__*/_defineComponent({
  __name: 'IssueTrackerIndex',
  setup(__props, { expose: __expose }) {
  __expose();

// @Vue
const $q = useQuasar()
const $store = useStore()
const directr = $store.getters['Services/Directr']
const $route = useRoute()
const $sys = $store.getters['Services/Sys']

const {
  activeView,
  currentParent,
  currentRoute
} = usePageUtil($route, __dirname)

const isMobile = computed(() => $q.screen.lt.md)
const link = ref('')

const views = ref(Object.entries(Types.IssueTrackerView).map(([label, id]) => ({ label, id })))
const state = reactive<Types.IssueTrackerState>({
  view: $route.params?.view === 'issues' ? { id: Types.IssueTrackerView.Issues, label: 'Issues' } : { id: Types.IssueTrackerView.Drafts, label: 'Drafts' },
  active: null,
  activeItem: null,
})

const Tracker = new IssueTracker($store, $q.notify)
onMounted(async () => {
  await Tracker.init($store)

  if (state.view?.id !== Types.IssueTrackerView.Issues && !Tracker.drafts.total && Tracker.issues.total)
    state.view = { id: Types.IssueTrackerView.Issues, label: 'Issues' }
})

const currentItem = computed(() => {
  if (state.view?.id === 'drafts') {
    return Tracker.dict.draft.value[state.active]?.raw
  }
  else if (state.view?.id === 'issues') {
    return Tracker.dict.issue.value[state.active]
  }
  return null
})

watch(() => Tracker.drafts.total + Tracker.issues.total, () => {
  const dIndex = Tracker.drafts.index(state.active)
  const iIndex = Tracker.drafts.index(state.active)
  if (dIndex < 0 && iIndex < 0) state.active = null
})

watch(() => state?.active, async value => {
  if (!state.active) return
  const channel = state.view?.id === 'drafts'
    ? Tracker.dict.draft.value[state.active].raw?.service
    : state.view?.id === 'issues'
      ? Tracker.dict.catalogue[Tracker.dict.issue.value[state.active].catalogue]?.service?.id
      : null
  if (channel) {
    const mirrorId = state.view?.id === 'drafts'
    ? Tracker.dict.draft.value[state.active].raw?.id
    : state.view?.id === 'issues'
      ? Tracker.dict.issue.value[state.active].id
      : null
    const services = await $sys.get('/service')
    const serviceConf = services?.find(s => s.id === channel) || ''
    if (serviceConf.type === 'shopify' && mirrorId) {
      link.value = `https://admin.shopify.com/store/${serviceConf.account.value}/orders/${mirrorId}`
    } else link.value = ''
  }
})

function edit (id: string) {
  state.active = id
}

function checkEdited () {
  if (!(state.activeItem && currentItem.value)) return true
  const diff = DeepDiff(state.activeItem, currentItem.value)
  if (!diff) return true
  else {
    let hasDifference = false
    diff.forEach(element => {
      hasDifference = false
      if (element.kind === 'N' || element.kind === 'D') return true
      else if (element.kind === 'E') {
        if (element.lhs !== element.rhs) {
          hasDifference = true
        }
      }
      else if (element.kind === 'A') return ['E', 'N', 'D'].includes(element.item.kind)
    })
    return hasDifference
  }
}

async function activateItem (activate: (id: string) => void, edit: (id: string) => void, id) {
  if (checkEdited()) {
    activate(id)
    edit(id)
    setActiveItem()
  }
  else {
    await confirmDiscard(activate, edit, id)
  }
}

const debouncedActivateItem = debounce(activateItem, 200)

function setActiveItem () {
  state.activeItem = JSON.parse(JSON.stringify(currentItem.value))
}

const confirmDiscard = async (activate?: (id: string) => void, edit?: (id: string) => void, id?): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    return $q.dialog({
      title: 'Discard unsaved changes?',
      message: 'Do you want to leave and discard your unsaved changes?',
      persistent: true,
      cancel: {
        label: 'Go back',
        flat: true,
        color: 'black',
      },
      ok: {
        label: 'Discard',
        color: 'negative',
      },
    }).onOk(() => {
      if (activate && edit && id)
      {
        activate(id)
        edit(id)
        setActiveItem()
      }
      else {
        resolve(true)
      }
    }).onCancel(() => {
      resolve(false)
    })
  })
}

onBeforeRouteLeave(async (to, from) => {
  if (!checkEdited()) return await confirmDiscard()
  return true
})


const __returned__ = { $q, $store, directr, $route, $sys, activeView, currentParent, currentRoute, isMobile, link, views, state, Tracker, currentItem, edit, checkEdited, activateItem, debouncedActivateItem, setActiveItem, confirmDiscard, computed, onMounted, reactive, ref, watch, get useStore() { return useStore }, get useRoute() { return useRoute }, get onBeforeRouteLeave() { return onBeforeRouteLeave }, get onBeforeRouteUpdate() { return onBeforeRouteUpdate }, get CountryNames() { return CountryNames }, get CountriesByName() { return CountriesByName }, get PostalAddress() { return PostalAddress }, get usePageUtil() { return usePageUtil }, get IssueTracker() { return IssueTracker }, get Types() { return Types }, get DeepDiff() { return DeepDiff }, Page, TabNav, SelectableList, SelectableItem, ControllableView, DraftEditor, IssueEditor, AllClearImg, ChooseItem, get useQuasar() { return useQuasar }, get debounce() { return debounce } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})